import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBetaSignup } from '../context/BetaSignupContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Clock, ArrowRight } from 'lucide-react';
import { blogPosts } from '../mockData';

// Helper to get image URL (handle both URLs and placeholders)
const getImageUrl = (image) => {
  if (image && image.startsWith('http')) {
    return image;
  }
  // Fallback placeholder images if not a URL
  const placeholders = {
    henwy: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80',
    liquidity: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80',
    ai: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
  };
  return placeholders[image] || 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80';
};

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { openModal } = useBetaSignup();
  
  // Get unique categories from blog posts
  const allCategories = ['All', ...new Set(blogPosts.map(post => post.category))];
  
  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#F5F3ED]">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Our Thinking
          </h1>
          <p className="text-xl text-gray-600 font-light max-w-3xl mx-auto">
            Insights, strategies, and analysis from our investment team.
            Stay informed about market trends and wealth management best practices.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-b">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {allCategories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category)}
                className={`rounded-full px-6 font-light ${selectedCategory === category ? 'bg-[#1a1a3e] hover:bg-[#252550]' : ''}`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Link 
                key={post.id} 
                to={`/blog/${post.id}`}
                className="block"
                data-testid={`blog-card-${post.id}`}
              >
                <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group border-0 h-full">
                  <div className="h-48 bg-gray-200 overflow-hidden">
                    <img 
                      src={getImageUrl(post.image)} 
                      alt={post.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="inline-block px-3 py-1 bg-[#FF8C42] text-white text-xs font-medium rounded-full mb-3">
                      {post.category}
                    </div>
                    <h3 className="text-xl font-light mb-3 group-hover:text-gray-600 transition-colors" style={{ fontFamily: 'Georgia, serif' }}>
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-600 font-light mb-4">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500 font-light">
                      <span>By {post.author}</span>
                      <div className="flex items-center">
                        <Clock size={14} className="mr-1" />
                        {post.readTime}
                      </div>
                    </div>
                    <div className="mt-4 p-0 h-auto font-light group-hover:translate-x-1 transition-transform flex items-center text-[#1a1a3e]">
                      Read More <ArrowRight size={16} className="ml-1" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 font-light">No articles found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#F5F3ED]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-4xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>Stay Informed</h2>
          <p className="text-gray-600 font-light mb-8">
            Get weekly insights and market updates delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#FF8C42] font-light"
            />
            <Button 
              onClick={openModal}
              className="bg-[#1a1a3e] hover:bg-[#252550] rounded-full px-8 font-light"
            >
              Subscribe
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1a1a3e] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Ready to grow your wealth?
          </h2>
          <Button 
            size="lg" 
            onClick={openModal}
            className="bg-[#FF8C42] text-white hover:bg-[#FFB366] px-10 py-7 rounded-full font-light"
          >
            BECOME A CLIENT
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Blog;
